import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import { required } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import router from "@/router";
import axios from "@axios";
import {
  computed,
  onMounted,
  ref,
  reactive,
  getCurrentInstance,
  nextTick
} from "@vue/composition-api";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { image } from "vee-validate/dist/rules";
import useFlowchartValidations from "@/composables/flowchartValidations"

export default function useCustomCommandConfig() {

  const isUpdating = ref(false);
  const idActionPlan = ref(null);
  const actionPlanName = ref("");
  const idTenant = ref([]);
  const idProfile = ref([]);
  const defaultActionPlan = ref(false);
  const selectedAlarmType = ref([]);
  const selectedStatus = ref([]);
  const allAlarmTypes = ref(false);
  const commentsEnabled = ref(false);
  const filesEnabled = ref(false);
  const commandsLoaded = ref(false);
  const isFlowchartReady = ref(false);

  const { refFormObserver, getValidationState } = formValidation();

  const toast = useToast();
  const { t } = useI18nUtils();
  const vm = getCurrentInstance().proxy;
  let flowchartEditor = null;

  const MAX_NODE_CONNECTIONS = 3;
  const flowchartError = ref(null);
  const missingNodeData = ref([]);
  const invalidNodes = ref([]);

  const trans = {
    'commands.messages.success_created': t('commands.messages.success_created'),
    'commands.messages.success_updated': t('commands.messages.success_updated'),
    'action_plan.messages.alarm_type_required': t('action_plan.messages.alarm_type_required'),
    'action_plan.messages.flowchart_not_ready': t('action_plan.messages.flowchart_not_ready'),
  };

  // Computed Properties ------------------------------------
  const commandOptions = computed(() => store.getters['command/getSelectedItems']
    .map(c => ({
      value: c.value,
      label: c.label,
      cmd: c.cmd,
      requirements: c.requirements,
      custom: !!c.id_user
    })));
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems']);
  const profileOptions = computed(() => store.getters['profile/getSelectedItems']);
  const alarmTypeOptions = computed(() => store.getters['alarm/getAlarmTypes']);
  const statusOptions = computed(() => [
    { value: 'attending', label: t('action_plan.fields.action_plan_status.options.attending') },
    { value: 'waiting', label: t('action_plan.fields.action_plan_status.options.waiting') },
    { value: 'IN_REVIEW', label: 'En Revisión del supervisor' },
    { value: 'AWAITING_CHANGES', label: 'En Revisión del operador' },
    { value: 'IN_MAINTENANCE', label: 'En flujo de mantenimiento' },
  ]);

  const toIdList = (items) => {
    if (items == null) return [];

    return (Array.isArray(items) ? items : [items])
      .map((item) => (item && typeof item === 'object' ? item.id ?? item.value : item))
      .filter((item) => item != null);
  };

  const toggleAllAlarmTypes = (enabled) => {
    if (enabled) selectedAlarmType.value = [];
  };

  const isViewerOptionEnabled = (value) => [true, 1, '1'].includes(value);

  // Methods ------------------------------------
  const onFlowchartReady = (editor) => {
    flowchartEditor = editor;
    isFlowchartReady.value = true;

    if (router.currentRoute.params.id_action_plan) {
      isUpdating.value = true;
      idActionPlan.value = router.currentRoute.params.id_action_plan
      loadUpdateData(idActionPlan.value);
    }
  }

  const loadInitData = async () => {
    const commandsRequest = store.dispatch('command/getAll');
    store.dispatch('tenant/getAll');
    store.dispatch('profile/getAll');
    store.dispatch('alarm/getAlarmTypes');

    await commandsRequest;
    commandsLoaded.value = true;
  }

  const loadUpdateData = async (idActionPlan) => {
    const { data } = await store.dispatch("action_plan/show", idActionPlan);
    actionPlanName.value = data.name;
    idTenant.value = toIdList(data.id_tenants ?? data.tenants ?? data.id_tenant ?? data.tenant);
    idProfile.value = toIdList(data.id_profiles ?? data.profiles ?? data.id_profile ?? data.profile);
    defaultActionPlan.value = data.default;
    allAlarmTypes.value = [true, 1, '1'].includes(data.all_alarms);
    selectedAlarmType.value = toIdList(data.alarm_types ?? data.alarm_type);
    selectedStatus.value = toIdList(data.alarm_status);
    commentsEnabled.value = isViewerOptionEnabled(
      data.setup?.viewer_options?.comments_enabled
    );
    filesEnabled.value = isViewerOptionEnabled(
      data.setup?.viewer_options?.files_enabled
    );

    if (data.setup) {
      await flowchartEditor.importState(data.setup);
    }
  };

  const validateFlowchart = ({ drawflow }) => {

    const nodes = Object
      .values(drawflow.Home.data)
      .sort((a, z) => a.pos_x - z.pos_x);

    invalidNodes.value = [];
    missingNodeData.value = [];

    const { checkEmptyConnections, checkNodeValidity } = useFlowchartValidations(nodes, invalidNodes, missingNodeData);

    nodes.forEach(node => {
      checkEmptyConnections(node);
      checkNodeValidity(node);
    });

    flowchartError.value = invalidNodes.value.length ?
      invalidNodes.value[0] :
      null;

    return invalidNodes.value.length === 0 && missingNodeData.value.length === 0;
  };

  const save = async () => {
    const validation = await refFormObserver.value.validate();
    const hasAlarmTypes = Array.isArray(selectedAlarmType.value)
      && selectedAlarmType.value.length > 0;

    if (!allAlarmTypes.value && !hasAlarmTypes) {
      refFormObserver.value.setErrors({
        alarm_type: [trans['action_plan.messages.alarm_type_required']],
      });
      return false;
    }

    if (!isFlowchartReady.value || !flowchartEditor) {
      toast({
        component: ToastificationContent,
        props: {
          title: trans['action_plan.messages.flowchart_not_ready'],
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        },
      });
      return false;
    }

    await nextTick();
    const flowChartData = flowchartEditor.exportState();

    if (!validateFlowchart(flowChartData)) return false;

    const data = {
      name: actionPlanName.value,
      default: defaultActionPlan.value,
      setup: {
        ...flowChartData,
        viewer_options: {
          comments_enabled: commentsEnabled.value,
          files_enabled: filesEnabled.value,
        },
      },
      all_alarms: allAlarmTypes.value ? 1 : 0,
      alarm_type: allAlarmTypes.value ? null : selectedAlarmType.value,
      alarm_status: selectedStatus.value,
      id_tenant: idTenant.value,
      id_profiles: idProfile.value?.length ? idProfile.value : null,
    }

    if (validation) {

      if (!isUpdating.value) {

        store.dispatch("action_plan/add", data).then(data => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans['commands.messages.success_created'],
              icon: "CheckIcon",
              variant: "success",
            },
          });

          isUpdating.value = true;
          idActionPlan.value = data.data.id;

          router.push({ path: `/action-plan/config/${idActionPlan.value}` });
        });

      } else {

        store.dispatch("action_plan/edit", { id: idActionPlan.value, params: data }).then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans['commands.messages.success_updated'],
              icon: "CheckIcon",
              variant: "success",
            },
          });
        });

      }
    }

    return true;
  };

  const showActionPlanPreviewModal = async () => {
    const status = await save();
    if (!status) return;

    vm.$root.$emit('bv::show::modal', 'action_plan_preview_modal');
  };

  // Hooks ------------------------------------
  onMounted(async () => await loadInitData());

  return {
    isUpdating,
    actionPlanName,
    idTenant,
    idProfile,
    profileOptions,
    idActionPlan,
    selectedAlarmType,
    alarmTypeOptions,
    selectedStatus,
    allAlarmTypes,
    commentsEnabled,
    filesEnabled,
    commandsLoaded,
    defaultActionPlan,
    commandOptions,
    tenantOptions,
    refFormObserver,
    getValidationState,
    required,
    save,
    onFlowchartReady,
    flowchartError,
    missingNodeData,
    showActionPlanPreviewModal,
    statusOptions,
    toggleAllAlarmTypes,
  };
}
